import React, { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { LeaResultPanel } from "./LeaResultPanel";

const QUEUE_LABELS = {
  new: { fr: "Nouveaux", en: "New", es: "Nuevos" },
  vytali_qualified: { fr: "Vytali Qualified", en: "Vytali Qualified", es: "Vytali Qualified" },
  to_qualify_by_lea: { fr: "À qualifier par Léa", en: "To qualify by Léa", es: "Para calificar por Léa" },
  lea_in_progress: { fr: "Léa en cours", en: "Léa in progress", es: "Léa en curso" },
  interview_ready: { fr: "Interview Ready", en: "Interview Ready", es: "Interview Ready" },
  interview_scheduled: { fr: "Entrevue planifiée", en: "Interview scheduled", es: "Entrevista planificada" },
  human_review: { fr: "Human Review", en: "Human Review", es: "Human Review" },
  follow_up: { fr: "Follow-up", en: "Follow-up", es: "Seguimiento" },
  nurture: { fr: "Nurture", en: "Nurture", es: "Nurture" },
  closed: { fr: "Closed / Not now", en: "Closed / Not now", es: "Cerrado / No ahora" },
};

const label = (map, key, lang) => (map[key] || {})[lang] || (map[key] || {}).fr || key;

export default function AdminRecruitOps() {
  const { lang } = useI18n();
  const [queues, setQueues] = useState([]);
  const [active, setActive] = useState("new");
  const [items, setItems] = useState([]);
  const [review, setReview] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [lea, setLea] = useState(null);
  const [tab, setTab] = useState("queues");
  const [busy, setBusy] = useState(null);
  const [openLea, setOpenLea] = useState(null);
  const [leaResults, setLeaResults] = useState({});

  const toggleLea = async (applicationId) => {
    if (openLea === applicationId) {
      setOpenLea(null);
      return;
    }
    setOpenLea(applicationId);
    if (leaResults[applicationId]) return;
    try {
      const res = await api.get(`/admin/recruit/applications/${applicationId}/overview`);
      setLeaResults((prev) => ({ ...prev, [applicationId]: res.data }));
    } catch (error) {
      setOpenLea(null);
      toast.error(apiError(error, "Résultat Léa indisponible"));
    }
  };

  const loadQueues = () => api.get("/admin/recruit/queues").then((r) => setQueues(r.data.queues));
  const loadItems = (key) => api.get(`/admin/recruit/queues/${key}`).then((r) => setItems(r.data.items));
  const loadReview = () => api.get("/admin/recruit/human-review").then((r) => setReview(r.data.items));

  useEffect(() => {
    loadQueues();
    loadItems(active);
    loadReview();
    api.get("/admin/recruit/campaigns").then((r) => setCampaigns(r.data.items));
    api.get("/admin/recruit/lea-status").then((r) => setLea(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openQueue = (key) => {
    setActive(key);
    loadItems(key);
  };

  const act = async (applicationId, call) => {
    setBusy(applicationId);
    try {
      await call();
      await Promise.all([loadQueues(), loadItems(active), loadReview()]);
    } catch (error) {
      toast.error(apiError(error, "Erreur"));
    } finally {
      setBusy(null);
    }
  };

  const evaluate = (id) => act(id, async () => {
    const res = await api.post(`/admin/recruit/applications/${id}/evaluate`);
    toast.success(res.data.qualification.qualified
      ? "Vytali Qualified"
      : `Non qualifié : ${res.data.qualification.blocking_reasons.join(", ")}`);
  });

  const resolveReview = (id) => act(id, async () => {
    await api.post(`/admin/recruit/applications/${id}/human-review/resolve`,
      { resolution: "cleared", note: "Résolu depuis la console" });
    toast.success("Human Review résolu");
  });

  const setNurture = (id) => act(id, async () => {
    await api.post(`/admin/recruit/applications/${id}/nurture`, { reason: "wants_later_contact" });
    toast.success("Placé en Nurture");
  });

  return (
    <div data-testid="admin-recruit-ops" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-light">Recruitment Operations</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Campagnes → Intake → Vytali Qualified → Léa → Interview Ready. Human Review est une route d'exception.
        </p>
        {lea && (
          <p data-testid="admin-recruit-lea-status" className="mt-3 border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
            Léa : {lea.lea.outbound} · appels automatiques : {String(lea.lea.auto_call)} · calendrier : {lea.interview_scheduling.status}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {[["queues", "Files"], ["review", "Human Review"], ["campaigns", "Campagnes"]].map(([key, text]) => (
          <button
            key={key}
            type="button"
            data-testid={`admin-recruit-tab-${key}`}
            onClick={() => setTab(key)}
            className={`border px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${
              tab === key ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      {tab === "queues" && (
        <div className="space-y-4">
          <div data-testid="admin-recruit-queues" className="flex flex-wrap gap-2">
            {queues.map((q) => (
              <button
                key={q.key}
                type="button"
                data-testid={`admin-recruit-queue-${q.key}`}
                onClick={() => openQueue(q.key)}
                className={`border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                  active === q.key ? "border-accent bg-accent text-accent-foreground" : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                {label(QUEUE_LABELS, q.key, lang)} ({q.count})
              </button>
            ))}
          </div>

          <ul data-testid="admin-recruit-items" className="divide-y divide-border border border-border bg-surface">
            {items.length === 0 && (
              <li className="px-4 py-6 text-sm text-muted-foreground">Aucune candidature dans cette file.</li>
            )}
            {items.map((item) => (
              <li key={item.application_id} data-testid={`admin-recruit-item-${item.application_id}`} className="space-y-2 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{item.first_name} {item.last_name}</span>
                  <span className="text-xs text-muted-foreground">{item.city} {item.province_state}</span>
                  <span className="border border-border px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {item.recruitment_state}
                  </span>
                  {item.qualified === true && (
                    <span className="inline-flex items-center gap-1 border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-emerald-800">
                      <CheckCircle2 className="h-3 w-3" /> Qualified
                    </span>
                  )}
                  {item.human_review?.status === "open" && (
                    <span className="inline-flex items-center gap-1 border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-900">
                      <AlertTriangle className="h-3 w-3" /> {item.human_review.reason}
                    </span>
                  )}
                  {item.nurture && (
                    <span className="inline-flex items-center gap-1 border border-border px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <Clock className="h-3 w-3" /> {item.nurture.reason}
                    </span>
                  )}
                </div>
                {item.fit_top?.length > 0 && (
                  <p data-testid={`admin-recruit-fit-${item.application_id}`} className="text-[11px] uppercase tracking-wider text-accent">
                    {item.fit_top.join(" · ")}
                  </p>
                )}
                {item.blocking_reasons?.length > 0 && (
                  <p className="text-xs text-muted-foreground">Bloquants : {item.blocking_reasons.join(", ")}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <button type="button" data-testid={`admin-recruit-evaluate-${item.application_id}`}
                    disabled={busy === item.application_id} onClick={() => evaluate(item.application_id)}
                    className="border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-muted disabled:opacity-60">
                    Évaluer
                  </button>
                  <button type="button" data-testid={`admin-recruit-nurture-${item.application_id}`}
                    disabled={busy === item.application_id} onClick={() => setNurture(item.application_id)}
                    className="border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-muted disabled:opacity-60">
                    Nurture
                  </button>
                  <button type="button" data-testid={`admin-recruit-lea-toggle-${item.application_id}`}
                    aria-expanded={openLea === item.application_id}
                    onClick={() => toggleLea(item.application_id)}
                    className="border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-muted focus-visible:ring-2 focus-visible:ring-accent">
                    {openLea === item.application_id ? "Masquer Léa" : "Résultat Léa"}
                  </button>
                </div>
                {openLea === item.application_id && leaResults[item.application_id] && (
                  <LeaResultPanel
                    applicationId={item.application_id}
                    result={leaResults[item.application_id].lea_result}
                    scheduling={leaResults[item.application_id].interview_scheduling}
                    timeline={leaResults[item.application_id].timeline}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "review" && (
        <ul data-testid="admin-recruit-review" className="divide-y divide-border border border-border bg-surface">
          {review.length === 0 && (
            <li data-testid="admin-recruit-review-empty" className="px-4 py-6 text-sm text-muted-foreground">
              Aucun dossier en Human Review.
            </li>
          )}
          {review.map((item) => (
            <li key={item.application_id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <span className="font-medium">{item.first_name} {item.last_name}</span>
              <span className="border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-900">
                {item.reason}
              </span>
              <span className="text-xs text-muted-foreground">{item.detail}</span>
              <button type="button" data-testid={`admin-recruit-resolve-${item.application_id}`}
                disabled={busy === item.application_id} onClick={() => resolveReview(item.application_id)}
                className="ml-auto border border-border px-3 py-1 text-xs uppercase tracking-wider hover:bg-muted disabled:opacity-60">
                Résoudre
              </button>
            </li>
          ))}
        </ul>
      )}

      {tab === "campaigns" && (
        <ul data-testid="admin-recruit-campaigns" className="divide-y divide-border border border-border bg-surface">
          {campaigns.length === 0 && (
            <li data-testid="admin-recruit-campaigns-empty" className="px-4 py-6 text-sm text-muted-foreground">
              Aucune campagne de recrutement configurée.
            </li>
          )}
          {campaigns.map((c) => (
            <li key={c.id} data-testid={`admin-recruit-campaign-${c.id}`} className="space-y-1 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Megaphone className="h-3.5 w-3.5 text-accent" />
                <span className="font-medium">{c.internal_name}</span>
                <span className="border border-border px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                  {c.opportunity_type}
                </span>
                <span className={`border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
                  c.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-border text-muted-foreground"
                }`}>
                  {c.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {c.language?.toUpperCase()} {c.region} · UTM {c.utm_identifiers?.utm_campaign || "—"} ·
                règles : {Object.keys(c.qualification_rules || {}).length || "aucune (core seulement)"}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
